// brain.ts — the Brain screen (#/brain): the Bridge's admin view of the
// organisation brain and its gardener (P20; design §8r "The admin surface").
//
// Five panes on one admin page: BROWSE, the landing view (brain-browse.ts:
// the type list with counts, the entity table, search and tag filter — every
// id a link), the gardener's RUNS with their measured
// lines (and a REVERT per landed run — one git revert, nothing waits on a
// person), the DIGEST (watermark, alarm, contested pairs, flagged
// contradictions, merges), an ENTITY page reached from the browse table or by
// id (claims with their sources, valid-from and what invalidated them; the
// graph around it, every related entity a link — the graph is walked by
// clicking), and the PROPOSALS log. An admin EDIT goes through the same door
// an agent writes through — the page never touches the ledger: INVALIDATE a
// claim, ADD a fact (a claim or a relation), or REPLACE a fact (the old claim
// invalidated and the new one added in one submission; brain-facts.ts).
//
// Same idiom as users.ts/audit.ts: the server decides, refusals render
// verbatim, colours come from --hc-* tokens only.

import type { ColDef, GridApi } from 'ag-grid-community';
import { el, jsonFetch, makeGrid, renderRefusal, relativeTime } from './admin-ui.js';
import { mkIconBtn, setBtnState } from './buttons.js';
import { destroyDialog, openDialog, showDialogMsg } from './dialog.js';
import { mkFcText, mkFcToggle } from './form-controls.js';
import { mountShell } from './shell.js';
import { entityLink, mountBrowse, typeFromHash } from './brain-browse.js';
import { factRefusal, factRequest, type FactInput, type FactOp } from './brain-facts.js';

interface RunRow { id: string; status: string; read_sha: string; landed_sha: string | null; produced_sha: string | null; refused_by: string | null; measured_line: string | null; model: string | null; launcher: string | null; cost_usd: string | null; started_at: string; ended_at: string | null; trigger_commits: number; trigger_lines: number }
interface Overview { organisation: string; branch: string; seat_line: string; armed: boolean; digest: { state: { last_gardened_sha: string | null; consecutive_noops: number; alarm: string | null }; contested: Array<{ proposal: string; edge_to: string; invalidations: number }>; flagged: Array<{ entity: string; claim: string; property: string }>; merged: string[] } | null }
interface EntityPage { entity: { id: string; type: string; name?: string; proposed_type?: string; tags?: string[]; path: string }; claims: Array<{ id: string; property: string; value?: unknown; to?: string; status: string; valid_from: string; invalidated_at?: string; invalidated_by?: string; live: boolean; tags?: string[]; sources_resolved: Array<{ id: string; title?: string; as_of?: string; missing?: boolean }> }>; relations: Array<{ property: string; to: string }>; inbound: Array<{ from: string; properties: string[] }>; graph: { two_hops: string[]; same_as_union: string[] } }
type ClaimRow = EntityPage['claims'][number];
interface Proposal { id: string; proposed_type?: string; nearest_type?: string; status?: string; edge?: string; edge_to?: string; edge_status?: string; edge_by?: string; edge_invalidations?: number; run: { id: string; status: string } | null; revertable: boolean }

export function statusFamily(status: string): 'ok' | 'warn' | 'bad' | 'live' {
  if (status === 'landed' || status === 'noop') return 'ok';
  if (status === 'refused') return 'bad';
  if (status === 'reverted') return 'warn';
  return 'live';
}

export function shortSha(s: string | null | undefined): string { return s ? s.slice(0, 12) : '—'; }

/** A refusal's own words, verbatim (empty when the body carried none). */
export function errOf(data: unknown): string { return data && typeof data === 'object' && 'error' in data ? ` ${String((data as { error: unknown }).error)}` : ''; }

export function mountBrain(root: HTMLElement): () => void {
  const shell = mountShell(root, 'brain');
  const main = shell.main;
  const modal = shell.modal;
  main.innerHTML = '';
  shell.pagebar.innerHTML = `<h1 class="hc-pagetitle">Brain</h1><span class="hc-pagecount" id="hcAdminStatus"></span>`;
  const status = shell.pagebar.querySelector('#hcAdminStatus') as HTMLElement;
  const wrap = el('div', 'hc-brain');
  main.appendChild(wrap);
  const section = (title: string, id: string): HTMLElement => { const s = el('section', 'hc-brain-sec'); s.id = id; s.appendChild(el('h2', 'hc-brain-h', title)); const body = el('div', 'hc-brain-body'); s.appendChild(body); wrap.appendChild(s); return body; };
  const browseBody = section('Browse', 'brainBrowse');
  const digestBody = section('Gardener digest', 'brainDigest');
  const runsBody = section('Gardener runs', 'brainRuns');
  const entityBody = section('Entity', 'brainEntity');
  const propsBody = section('Proposals log', 'brainProposals');
  // On #/brain/entity/<id> the entity is what was asked for: its section
  // leads; everywhere else Browse is the landing view.
  if (/^#\/brain\/entity\//.test(location.hash)) wrap.prepend(entityBody.parentElement as HTMLElement);
  // The lookup form stays put above the entity view (renderEntity used to
  // clear the whole section, lookup included).
  const entityView = el('div', 'hc-brain-entity');
  let disposed = false;
  // The two tables ride makeGrid (the console's railed AG grid — the same
  // lane users.ts/audit.ts mount), never a bare <table>: the primitive
  // catalog's bare-grid arm is pinned at 0 and refuses a hand-rolled grid.
  let runsGrid: GridApi<RunRow> | null = null;
  let claimsGrid: GridApi<ClaimRow> | null = null;

  const loadOverview = async () => {
    const r = await jsonFetch<Overview>('GET', '/api/admin/brain/overview');
    if (disposed) return;
    if (r.status !== 200 || !r.data) { renderRefusal(main, r.status); status.textContent = `overview ${r.status}`; return; }
    const d = r.data.digest;
    digestBody.innerHTML = '';
    digestBody.appendChild(el('p', 'hc-brain-seat', r.data.seat_line));
    if (d) {
      const st = d.state;
      digestBody.appendChild(el('p', 'hc-brain-state', `watermark ${shortSha(st.last_gardened_sha) || '(root)'} · consecutive no-ops ${st.consecutive_noops}${st.alarm ? ' · ' + st.alarm : ''}`));
      if (st.alarm) digestBody.lastElementChild!.classList.add('hc-brain-alarm');
      const ul = el('ul', 'hc-brain-list');
      for (const c of d.contested) ul.appendChild(el('li', 'hc-brain-contested', `contested: ${c.proposal} ~ ${c.edge_to} (${c.invalidations} invalidations; both definitions live)`));
      for (const f of d.flagged) ul.appendChild(el('li', 'hc-brain-flagged', `contradiction: ${f.entity} / ${f.property} (claim ${f.claim})`));
      for (const m of d.merged) ul.appendChild(el('li', 'hc-brain-merged', `merged: ${m}`));
      if (!ul.childElementCount) ul.appendChild(el('li', '', 'nothing contested, flagged or merged'));
      digestBody.appendChild(ul);
    }
    status.textContent = `${r.data.organisation} · ${r.data.branch} · gardener ${r.data.armed ? 'armed' : 'off'}`;
  };
  const loadRuns = async () => {
    const r = await jsonFetch<{ runs: RunRow[]; count: number }>('GET', '/api/admin/brain/runs');
    if (disposed) return;
    runsGrid?.destroy(); runsGrid = null;
    runsBody.innerHTML = '';
    if (r.status !== 200 || !r.data) { runsBody.appendChild(el('p', 'hc-brain-refusal', `runs: ${r.status}${errOf(r.data)}`)); return; }
    if (!r.data.runs.length) { runsBody.appendChild(el('p', '', 'no runs yet')); return; }
    const host = el('div', 'hc-grid hc-brain-grid');
    runsBody.appendChild(host);
    const defs: ColDef<RunRow>[] = [
      { colId: 'when', headerName: 'When', width: 130, sort: 'desc', valueGetter: (p) => p.data?.started_at ?? '', cellRenderer: (p: { data?: RunRow }) => relativeTime(p.data?.started_at ?? null) },
      { colId: 'status', headerName: 'Status', width: 110, field: 'status' },
      { colId: 'read', headerName: 'Read', width: 130, cellClass: 'hc-mono', valueGetter: (p) => shortSha(p.data?.read_sha) },
      { colId: 'landed', headerName: 'Landed', width: 130, cellClass: 'hc-mono', valueGetter: (p) => shortSha(p.data?.landed_sha) },
      { colId: 'model', headerName: 'Model', width: 160, valueGetter: (p) => p.data?.model ?? '—' },
      { colId: 'cost', headerName: 'Cost', width: 110, valueGetter: (p) => !p.data ? '' : p.data.cost_usd === null ? 'unmeasured' : `$${Number(p.data.cost_usd).toFixed(4)}` },
      { colId: 'line', headerName: 'Measured line', flex: 2, minWidth: 260, sortable: false, cellClass: 'hc-brain-line', valueGetter: (p) => p.data?.measured_line ?? p.data?.refused_by ?? '(in flight)' },
      {
        colId: 'act', headerName: '', width: 64, sortable: false, resizable: false,
        cellRenderer: (p: { data?: RunRow }) => {
          const wrap = el('span', 'hc-rowact-wrap');
          const run = p.data;
          if (run && run.status === 'landed') {
            const b = mkIconBtn({ glyph: 'undo-2', label: 'Revert', intent: 'destructive', size: 'sm', cls: 'hc-rowact' });
            b.addEventListener('click', async () => { b.disabled = true; const rr = await jsonFetch<{ line: string }>('POST', `/api/admin/brain/runs/${run.id}/revert`, {}); runsBody.appendChild(el('p', 'hc-brain-line', rr.data?.line ?? `revert ${rr.status}`)); await loadRuns(); await loadOverview(); });
            wrap.appendChild(b);
          }
          return wrap;
        },
      },
    ];
    runsGrid = makeGrid<RunRow>(host, {
      columnDefs: defs,
      rowData: r.data.runs,
      getRowClass: (p) => (p.data ? `hc-brain-run hc-brain-run-${statusFamily(p.data.status)}` : ''),
    });
  };
  // The edit dialogs ride the console's ONE dialog engine (dialog.ts): a
  // system prompt is a banned modal (the primitive catalog's native-dialog
  // arm). Each names what it changes and asks why; the note is recorded in
  // the minted admin source. A refusal — the route's or the door's — renders
  // verbatim in the dialog (factRefusal).
  const factDialog = (op: FactOp, entityId: string, claim: ClaimRow | null): void => {
    openDialog(modal, (body, close) => {
      const title = op === 'invalidate' ? 'Invalidate claim' : op === 'supersede' ? 'Replace fact' : 'Add fact';
      body.appendChild(el('h2', 'hc-dialog-title', title));
      const lede = op === 'invalidate' ? `${entityId} / ${claim?.id} — why is this claim wrong? The note is recorded as the correction source; the claim is invalidated, never edited.`
        : op === 'supersede' ? `${entityId} / ${claim?.id} (${claim?.property}) — the old claim is invalidated and the new one added in one submission; neither is edited.`
        : `${entityId} — a new claim (a value, or a target entity id) or a relation to another entity. It lands through the same door an agent's write does.`;
      body.appendChild(el('p', 'hc-dialog-body', lede));
      const form = el('form', 'hc-form');
      const fields: Record<string, ReturnType<typeof mkFcText>> = {};
      const field = (key: string, label: string, placeholder: string) => { const f = mkFcText({ label, placeholder }); f.input.name = key; fields[key] = f; form.appendChild(f.el); return f; };
      if (op === 'add') { field('property', 'Property', 'e.g. description, url, memberOf'); field('value', 'Value', 'the value (leave empty for a target entity)'); field('to', 'Target entity id', 'links to an entity (a relation-shaped claim)'); }
      if (op === 'supersede') field(claim?.to !== undefined ? 'to' : 'value', claim?.to !== undefined ? 'New target entity id' : 'New value', claim?.to !== undefined ? `replaces → ${claim?.to}` : `replaces ${JSON.stringify(claim?.value ?? null)}`);
      if (op !== 'invalidate') field('valid_from', 'Valid from', 'YYYY-MM-DD (default today)');
      const relation = op === 'add' ? mkFcToggle({ label: 'As a relation', text: 'Add {property → target} as a relation, not a claim', checked: false }) : null;
      if (relation) form.appendChild(relation.el);
      const note = field('note', 'Why', op === 'invalidate' ? 'why the claim is wrong' : 'where this fact comes from');
      note.input.maxLength = 2000;
      (op === 'add' ? fields.property : op === 'supersede' ? (fields.value ?? fields.to) : note)?.input.setAttribute('data-autofocus', '');
      const msg = el('p', 'hc-dialog-msg');
      form.appendChild(msg);
      const actions = el('div', 'hc-dialog-actions');
      const submit = op === 'invalidate'
        ? mkIconBtn({ glyph: 'circle-x', label: 'Invalidate', intent: 'destructive', ctx: 'brain-correction-dialog', type: 'submit', noConfirm: 'the dialog IS the confirmation: it names the claim and asks why before anything is sent' })
        : op === 'supersede'
          ? mkIconBtn({ glyph: 'square-pen', label: 'Replace', intent: 'destructive', ctx: 'brain-correction-dialog', type: 'submit', noConfirm: 'the dialog IS the confirmation: it names the claim being replaced and asks why before anything is sent' })
          : mkIconBtn({ glyph: 'check', label: 'Add', type: 'submit' });
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      actions.append(cancel, submit);
      form.appendChild(actions);
      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const input: FactInput = { property: fields.property?.value, value: fields.value?.value, to: fields.to?.value, valid_from: fields.valid_from?.value, note: note.value, relation: relation?.checked ?? false };
        const req = factRequest(op, entityId, claim?.id ?? null, input);
        if (!req.ok) { showDialogMsg(msg, req.error); return; }
        setBtnState(submit, 'busy');
        void jsonFetch<{ error?: string; runLine?: string; refused_by?: string | null; correction?: { claims_edited: number; invalidated: number; added: number } }>('POST', '/api/admin/brain/corrections', req.body).then(async (rr) => {
          setBtnState(submit, 'enabled');
          const d = rr.data;
          if (rr.status >= 400 || !d || !d.correction) { showDialogMsg(msg, factRefusal(rr.status, d)); return; }
          close();
          entityView.appendChild(el('p', 'hc-brain-line', `${d.runLine ?? ''} — claims edited ${d.correction.claims_edited}, invalidated ${d.correction.invalidated}, added ${d.correction.added}`));
          await openEntity(entityId);
        });
      });
      body.appendChild(form);
    });
  };
  const renderEntity = (p: EntityPage) => {
    claimsGrid?.destroy(); claimsGrid = null;
    entityView.innerHTML = '';
    entityView.appendChild(el('h3', 'hc-brain-eh', `${p.entity.id} — ${p.entity.name ?? ''} (${p.entity.type}${p.entity.proposed_type ? ', proposed ' + p.entity.proposed_type : ''}) ${(p.entity.tags ?? []).map((t) => '#' + t).join(' ')}`));
    const addBtn = mkIconBtn({ glyph: 'plus', label: 'Add fact', cls: 'hc-brain-addfact' });
    addBtn.addEventListener('click', () => factDialog('add', p.entity.id, null));
    entityView.appendChild(addBtn);
    const host = el('div', 'hc-grid hc-brain-grid');
    entityView.appendChild(host);
    const defs: ColDef<ClaimRow>[] = [
      { colId: 'claim', headerName: 'Claim', width: 150, cellClass: 'hc-mono', field: 'id' },
      { colId: 'property', headerName: 'Property', width: 150, field: 'property' },
      { colId: 'value', headerName: 'Value', flex: 1, minWidth: 160, valueGetter: (p) => !p.data ? '' : p.data.to !== undefined ? `→ ${p.data.to}` : JSON.stringify(p.data.value ?? null), cellRenderer: (q: { data?: ClaimRow; value?: string }) => (q.data?.to !== undefined ? entityLink(q.data.to, `→ ${q.data.to}`) : q.value ?? '') },
      { colId: 'status', headerName: 'Status', width: 140, valueGetter: (p) => !p.data ? '' : `${p.data.status}${(p.data.tags ?? []).includes('contradiction') ? ' (contradiction)' : ''}` },
      { colId: 'sources', headerName: 'Sources', flex: 1, minWidth: 160, valueGetter: (p) => (p.data?.sources_resolved ?? []).map((s) => s.missing ? `${s.id}?` : `${s.id}${s.as_of ? ' @' + s.as_of : ''}`).join(', ') },
      { colId: 'valid_from', headerName: 'Valid from', width: 120, field: 'valid_from' },
      { colId: 'invalidated', headerName: 'Invalidated', width: 200, valueGetter: (p) => p.data?.invalidated_at ? `${p.data.invalidated_at} by ${p.data.invalidated_by}` : '—' },
      {
        colId: 'act', headerName: '', width: 96, sortable: false, resizable: false,
        cellRenderer: (q: { data?: ClaimRow }) => {
          const wrap = el('span', 'hc-rowact-wrap');
          const c = q.data;
          if (c && c.live) {
            const r = mkIconBtn({ glyph: 'square-pen', label: 'Replace fact', intent: 'destructive', size: 'sm', cls: 'hc-rowact', noConfirm: 'the click opens the replace dialog, which names the claim and asks for the new value and why' });
            r.addEventListener('click', () => factDialog('supersede', p.entity.id, c));
            const b = mkIconBtn({ glyph: 'circle-x', label: 'Invalidate', intent: 'destructive', size: 'sm', cls: 'hc-rowact', noConfirm: 'the click opens the correction dialog, which names the claim and asks why; the note is the correction source' });
            b.addEventListener('click', () => factDialog('invalidate', p.entity.id, c));
            wrap.append(r, b);
          }
          return wrap;
        },
      },
    ];
    claimsGrid = makeGrid<ClaimRow>(host, {
      columnDefs: defs,
      rowData: p.claims,
      getRowClass: (q) => (q.data ? (q.data.live ? 'hc-brain-claim-live' : 'hc-brain-claim-dead') : ''),
    });
    // Every related entity is a LINK to its own page: the graph is walked by
    // clicking (relations out, inbound, two hops, the sameAs union).
    const g = el('div', 'hc-brain-graph');
    const linkRow = (label: string, items: Array<{ id: string; pre?: string; post?: string }>) => {
      const row = el('p', 'hc-brain-graphrow', `${label}: `);
      if (!items.length) row.appendChild(document.createTextNode('none'));
      items.forEach((it, i) => {
        if (i) row.appendChild(document.createTextNode(', '));
        if (it.pre) row.appendChild(document.createTextNode(it.pre));
        row.appendChild(entityLink(it.id));
        if (it.post) row.appendChild(document.createTextNode(it.post));
      });
      g.appendChild(row);
    };
    linkRow('relations', p.relations.map((r) => ({ id: r.to, pre: `${r.property} → ` })));
    linkRow('inbound', p.inbound.map((i) => ({ id: i.from, post: ` (${i.properties.join(', ')})` })));
    linkRow('two hops', p.graph.two_hops.filter((id) => id !== p.entity.id).map((id) => ({ id })));
    linkRow('sameAs union', p.graph.same_as_union.filter((id) => id !== p.entity.id).map((id) => ({ id })));
    entityView.appendChild(g);
  };
  const openEntity = async (id: string) => {
    const r = await jsonFetch<EntityPage>('GET', `/api/admin/brain/entities/${encodeURIComponent(id)}`);
    if (disposed) return;
    if (r.status !== 200 || !r.data) { claimsGrid?.destroy(); claimsGrid = null; entityView.innerHTML = ''; entityView.appendChild(el('p', 'hc-brain-refusal', `entity ${id}: ${r.status}${errOf(r.data)}`)); return; }
    renderEntity(r.data);
  };
  const form = el('form', 'hc-brain-lookup');
  // The form-control primitive (the bare-input arm ratchets at its pin; a
  // new bare <input> is refused by name).
  const lookup = mkFcText({ label: 'Entity id', placeholder: 'entity id (e.g. org-0001)' });
  lookup.input.name = 'entity';
  const go = mkIconBtn({ glyph: 'search', label: 'Open', type: 'submit' });
  form.append(lookup.el, go);
  form.addEventListener('submit', (ev) => { ev.preventDefault(); const id = lookup.value.trim(); if (id) location.hash = `#/brain/entity/${encodeURIComponent(id)}`; });
  entityBody.appendChild(form);
  entityBody.appendChild(entityView);
  const loadProposals = async () => {
    const r = await jsonFetch<{ proposals: Proposal[] }>('GET', '/api/admin/brain/proposals');
    if (disposed) return;
    propsBody.innerHTML = '';
    if (r.status !== 200 || !r.data) { propsBody.appendChild(el('p', 'hc-brain-refusal', `proposals: ${r.status}${errOf(r.data)}`)); return; }
    if (!r.data.proposals.length) { propsBody.appendChild(el('p', '', 'no proposals')); return; }
    const ul = el('ul', 'hc-brain-list');
    for (const p of r.data.proposals) {
      const li = el('li', `hc-brain-prop hc-brain-prop-${p.edge_status ?? 'open'}`, `${p.proposed_type ?? p.id} → nearest ${p.nearest_type ?? '?'}: ${p.status ?? 'open'}${p.edge ? ` · edge ${p.edge} ${p.edge_to} (${p.edge_status}, ${p.edge_invalidations ?? 0} invalidations) by ${p.edge_by ?? '?'}` : ''}`);
      if (p.revertable && p.run) {
        const b = mkIconBtn({ glyph: 'undo-2', label: 'Revert run', intent: 'destructive' });
        b.addEventListener('click', async () => { b.disabled = true; const rr = await jsonFetch<{ line: string }>('POST', `/api/admin/brain/runs/${p.run!.id}/revert`, {}); li.appendChild(el('span', 'hc-brain-line', ` ${rr.data?.line ?? 'revert ' + rr.status}`)); await loadProposals(); await loadRuns(); });
        li.appendChild(b);
      }
      ul.appendChild(li);
    }
    propsBody.appendChild(ul);
  };
  const route = () => { const m = /^#\/brain\/entity\/(.+)$/.exec(location.hash); if (m) void openEntity(decodeURIComponent(m[1] as string)); };
  const browseTeardown = mountBrowse(browseBody, typeFromHash(location.hash));
  void loadOverview(); void loadRuns(); void loadProposals(); route();
  window.addEventListener('hashchange', route);
  return () => { disposed = true; browseTeardown(); window.removeEventListener('hashchange', route); runsGrid?.destroy(); runsGrid = null; claimsGrid?.destroy(); claimsGrid = null; destroyDialog(modal); shell.teardown(); };
}
